import { useCallback, useEffect, useRef, useState } from "react";
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  RowData,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import * as ShadcnTable from "@/v2/shared/shadcn-ui/table";
import { TableScrollArea } from "./TableScrollArea";
import { TableHeader } from "./TableHeader";
import { TableBody, TableBodyRef } from "./TableBody";
import { TableBodyLoader } from "./TableBodyLoader";
import { defaultCellRenderer } from "./cellRenderers";

interface TableProps<RowDataType extends RowData> {
  data: RowDataType[];
  columns: ColumnDef<RowDataType>[];
  loading?: boolean;
  sorting: SortingState;
  onSortingChange?: (SortingState: SortingState) => void;
  hasNextPage?: boolean;
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => Promise<void>;
  onRowClicked?: (row: RowDataType) => void;
  initialColumnVisibility?: Record<string, boolean>;
}

export function Table<RowDataType extends RowData>({
  data,
  columns,
  loading = false,
  sorting,
  onSortingChange,
  hasNextPage,
  isFetching,
  isFetchingNextPage,
  fetchNextPage,
  onRowClicked,
  initialColumnVisibility = {},
}: TableProps<RowDataType>) {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableBodyRef = useRef<TableBodyRef>(null);

  const [sortingState, setSortingState] = useState<SortingState>(sorting);
  const [columnVisibility, setColumnVisibility] = useState(
    initialColumnVisibility
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting: sortingState,
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    defaultColumn: {
      size: 160,
      minSize: 120,
      maxSize: 400,
      cell: defaultCellRenderer<RowDataType>,
    },
  });

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSortingState(updater);
    if (!!table.getRowModel().rows.length) {
      tableBodyRef.current?.scrollToIndex(0);
    }
  };

  table.setOptions((prev) => ({
    ...prev,
    onSortingChange: handleSortingChange,
  }));

  const { rows } = table.getRowModel();

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement && fetchNextPage) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;

        if (
          scrollHeight - scrollTop - clientHeight < 500 &&
          !isFetching &&
          hasNextPage
        ) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetching, hasNextPage]
  );

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  useEffect(() => {
    setSortingState(sorting);
  }, [sorting]);

  useEffect(() => {
    onSortingChange?.(sortingState);
  }, [onSortingChange, sortingState]);

  return (
    <TableScrollArea
      ref={tableContainerRef}
      handleScroll={(e) => fetchMoreOnBottomReached(e.currentTarget)}
      isLoadingMore={isFetchingNextPage}
      isEmpty={!loading && rows.length === 0}
    >
      <ShadcnTable.Table style={{ display: "grid", paddingInlineEnd: 16 }}>
        <TableHeader<RowDataType> headerGroups={table.getHeaderGroups()} />

        {loading ? (
          <TableBodyLoader<RowDataType>
            headerGroups={table.getHeaderGroups()}
          />
        ) : (
          <TableBody<RowDataType>
            ref={tableBodyRef}
            rows={rows}
            getScrollElement={() => tableContainerRef.current}
            onRowClicked={onRowClicked}
          />
        )}
      </ShadcnTable.Table>
    </TableScrollArea>
  );
}
